/**
 * The numbers above the Co-Living rooms list (Co-Living redesign, Slice 2;
 * fifth card added in UI1).
 *
 * THE NUMBERS ARE NOT NEW. Every value here is the page's existing `roomStats`
 * — the same client-side fold over `getRoomOccupancyStatus` the page has always
 * done, plus the unit count it already had in hand. This file is a different
 * way of drawing them and nothing else: no `*_rooms_snapshot`, no backend
 * aggregate, no second occupancy rule, and NO EXTRA REQUEST — the units were
 * loaded before any of this was drawn.
 *
 * THE PERCENTAGES ARE DERIVED FROM THOSE FOUR NUMBERS, so they cannot drift
 * from the counts beside them. They are rounded independently and therefore
 * need not sum to exactly 100 — a forced sum would have to lie about one of
 * the three.
 *
 * THE RING IS DECORATION OVER A NUMBER THAT IS ALREADY WRITTEN DOWN. The card
 * states its count and its percentage in text; the arc repeats the percentage
 * visually. Nothing here is conveyed by colour or by the arc alone.
 *
 * Tone classes mirror `apartments/UnitsKpiRow.js` so the two redesigned
 * sibling surfaces keep one vocabulary — Tailwind palette steps with a dark
 * counterpart each, no hex, nothing that stops following the theme.
 */
import React from "react";
import { BedDouble, Building2, CalendarClock, DoorOpen, UserCheck } from "lucide-react";

/*
 * `toPercent` is defined with the other pure display helpers rather than here
 * (UI2). The unit rows needed the same rule, and a helpers module whose stated
 * contract is "no React" must not reach into a component file to get it — so
 * the function moved to the module both callers can import, unchanged. Nothing
 * outside this file ever imported it from here, so it is not re-exported: one
 * module owns the name, and a grep for it lands there.
 */
import { toPercent } from "./roomDisplayHelpers";

const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/** Icon-tile tones, keyed by card — never looked up by the visible label. */
const TONE_CLASSES = {
  neutral: "bg-primary/10 text-primary dark:bg-primary/20",
  positive: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  vacant: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
};

/** Ring stroke colours, same tones, as text colour so `currentColor` picks them up. */
const RING_CLASSES = {
  positive: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  vacant: "text-rose-600 dark:text-rose-400",
};

export const CO_LIVING_KPI_CARDS = [
  {
    // FIRST, because it names the level the two below it are counted within:
    // this page's hierarchy is unit -> room, and the reader meets it in that
    // order. `Building2` is the same glyph `CoLivingUnitRow` puts on a unit,
    // so the card and the rows it summarises are recognisably the same thing.
    //
    // NO RING, for the same reason "Aktive Zimmer" has none: a ring shows a
    // SHARE of the total, and neither of these two IS a share — one is the
    // total and the other counts a different population entirely. An arc at
    // 100 % would invite the reader to look for the remaining 0 %.
    key: "units",
    label: "Einheiten",
    hint: "Co-Living-Einheiten",
    icon: Building2,
    tone: "neutral",
  },
  {
    key: "total",
    // NOT "Alle Zimmer", which is what this card said until OC4a while counting
    // something narrower. The number is `activeRooms.length` -- OC2b took retired
    // rooms out of it, because a room that has been stood down is not capacity --
    // so a portfolio with four rooms, one of them retired, read "Alle Zimmer 3"
    // beside four visible room cards. The count was right and the word was not.
    //
    // AND NOT "Zimmer im Betrieb" EITHER, which this card briefly carried. The
    // Co-Living dashboard already uses that phrase for `operating.totalRooms`:
    // active rooms in units INSIDE THE LEASE WINDOW. This page applies no lease
    // gate at all, so the same words would have named two different numbers on
    // two screens -- the species of problem OC4 exists to remove, not to add.
    // "Aktive Zimmer" names exactly what `isActiveRoom` decides and nothing more.
    //
    // The hint stays: "In allen Co-Living-Einheiten" names the SCOPE this card
    // covers (every unit, unfiltered), not the population it counts.
    label: "Aktive Zimmer",
    hint: "In allen Co-Living-Einheiten",
    icon: BedDouble,
    tone: "neutral",
  },
  {
    key: "occupied",
    label: "Belegt",
    hint: "Aktuell belegte Zimmer",
    icon: UserCheck,
    tone: "positive",
    ringLabelSuffix: "belegt",
  },
  {
    key: "reserved",
    label: "Reserviert",
    hint: "Reservierte Zimmer",
    icon: CalendarClock,
    tone: "warning",
    ringLabelSuffix: "reserviert",
  },
  {
    key: "free",
    label: "Frei",
    hint: "Freie Zimmer",
    icon: DoorOpen,
    tone: "vacant",
    ringLabelSuffix: "frei",
  },
];

function KpiIconTile({ icon: Icon, tone }) {
  return (
    <span
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${
        TONE_CLASSES[tone] || TONE_CLASSES.neutral
      }`}
    >
      <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
    </span>
  );
}

/** A hand-rolled arc — no chart library, no dependency, no animation. */
function KpiRing({ percent, tone, label }) {
  const safe = Math.max(0, Math.min(100, Number(percent) || 0));
  const dash = (safe / 100) * CIRCUMFERENCE;

  return (
    <span className="relative inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" role="img" aria-label={label}>
        <circle cx="50" cy="50" r={RADIUS} fill="none" strokeWidth="12" className="stroke-muted" />
        {safe > 0 ? (
          <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            strokeWidth="12"
            strokeLinecap="round"
            stroke="currentColor"
            className={RING_CLASSES[tone] || RING_CLASSES.positive}
            strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
          />
        ) : null}
      </svg>
      <span
        className="absolute text-[10.5px] font-semibold tabular-nums text-muted-foreground"
        aria-hidden="true"
      >
        {/* ONE DECIMAL, like every other occupancy figure (OC3). `safe` is a
            number, so 75 would print as "75" without this; the arc above uses
            the number, this prints the agreed string. */}
        {safe.toFixed(1)}%
      </span>
    </span>
  );
}

function CoLivingKpiCard({ label, value, hint, icon, tone, percent, ringLabelSuffix }) {
  const showRing = typeof percent === "number";

  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-border bg-card px-4 py-3.5 shadow-sm">
      <KpiIconTile icon={icon} tone={tone} />

      <div className="flex min-w-0 flex-1 flex-col">
        <dt className="break-words text-[10px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
          {label}
        </dt>
        <dd className="m-0 text-[22px] font-semibold leading-tight tabular-nums text-foreground">
          {value}
        </dd>
        <p className="m-0 mt-0.5 text-[10.5px] leading-snug text-muted-foreground">{hint}</p>
      </div>

      {showRing ? (
        <KpiRing percent={percent} tone={tone} label={`${percent} Prozent ${ringLabelSuffix}`} />
      ) : null}
    </div>
  );
}

export default function CoLivingKpiGrid({ stats }) {
  if (!stats) return null;

  const total = Number(stats.total) || 0;

  return (
    /*
      FIVE CARDS, AND THE SHELL DECIDES WHERE THEY BREAK. The admin frame is a
      288px sidebar plus 40px of padding either side, so the content is
      912px at `xl` and 1168px at `2xl` (capped at 1400px above that). Five
      columns at `xl` would give each card 173px — well under the ~219px the
      four-column row has been shipping at. So `xl` takes three (3 + 2) and the
      fifth column waits for `2xl`, where a card gets 224px and then 270px.
      Every step is at least as wide as what already works.
    */
    <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
      {CO_LIVING_KPI_CARDS.map((card) => (
        <CoLivingKpiCard
          key={card.key}
          label={card.label}
          value={Number(stats[card.key]) || 0}
          hint={card.hint}
          icon={card.icon}
          tone={card.tone}
          percent={card.ringLabelSuffix ? toPercent(stats[card.key], total) : undefined}
          ringLabelSuffix={card.ringLabelSuffix}
        />
      ))}
    </dl>
  );
}
